import { createContext, useContext, useReducer, useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import AuthService from "services/auth-service";

const MaterialUI = createContext();

export const AuthContext = createContext({
  isAuthenticated: false,
  login: () => {},
  logout: () => {},
});

const AuthContextProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      setIsAuthenticated(true);
      navigate(location.pathname);
    } else {
      navigate("/auth/login");
    }
  }, [navigate, location.pathname]);

  const login = (token, redirect=true) => {
    localStorage.setItem("token", token);
    setIsAuthenticated(true);
    if (redirect) {
      navigate("/dashboard");
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setIsAuthenticated(false);
    navigate("/auth/login");
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

MaterialUI.displayName = "MaterialUIContext";

function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value };
    }
    case "TRANSPARENT_SIDENAV": {
      return { ...state, transparentSidenav: action.value };
    }
    case "WHITE_SIDENAV": {
      return { ...state, whiteSidenav: action.value };
    }
    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value };
    }
    case "LAYOUT": {
      return { ...state, layout: action.value };
    }

    case "WALLET": {
      return { ...state, wallet: action.value };
    }
    case "WALLETS": {
      return { ...state, wallets: action.value };
    }
    case "TOGGLE_RELOAD_WALLETS": {
      return { ...state, toggleReloadWallets: action.value };
    }
    case "ACCOUNTS": {
      return { ...state, accounts: action.value };
    }
    case "TOGGLE_RELOAD_ACCOUNTS": {
      return { ...state, toggleReloadAccounts: action.value };
    }

    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function MaterialUIControllerProvider({ children }) {
  const initialState = {
    miniSidenav: false,
    transparentSidenav: false,
    whiteSidenav: false,
    transparentNavbar: true,
    layout: "dashboard",
    wallet: null,
    wallets: [],
    accounts: [],
    toggleReloadWallets: 1,
    toggleReloadAccounts: 1,
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  const loadWallets = async () => {
    const response = await AuthService.fetchWallets();
    setWallets(dispatch, response);
  };
  
  const loadAccounts = async () => {
    const response = await AuthService.fetchAccounts();
    setAccounts(dispatch, response);
  };

  useEffect(() => {
    if (controller.toggleReloadWallets > 1) {
      loadWallets();
    }
  }, [controller.toggleReloadWallets]);

  useEffect(() => {
    controller.wallet ? loadAccounts() : setAccounts(dispatch, []);
  }, [controller.wallet, controller.toggleReloadAccounts]);

  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>;
}

function useMaterialUIController() {
  const context = useContext(MaterialUI);

  if (!context) {
    throw new Error(
      "useMaterialUIController should be used inside the MaterialUIControllerProvider."
    );
  }

  return context;
}

MaterialUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setWhiteSidenav = (dispatch, value) => dispatch({ type: "WHITE_SIDENAV", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });

const setWallet = (dispatch, walletId) => dispatch({ type: "WALLET", value: walletId });
const setWallets = (dispatch, wallets) => dispatch({ type: "WALLETS", value: wallets });
const toggleReloadWallets = (dispatch, date) => dispatch({ type: "TOGGLE_RELOAD_WALLETS", value: date });
const setAccounts = (dispatch, accounts) => dispatch({ type: "ACCOUNTS", value: accounts });
const toggleReloadAccounts = (dispatch, date) => dispatch({ type: "TOGGLE_RELOAD_ACCOUNTS", value: date });

export {
  AuthContextProvider,
  MaterialUIControllerProvider,
  useMaterialUIController,
  
  setMiniSidenav,
  setLayout,
  setTransparentNavbar,
  setTransparentSidenav,
  setWhiteSidenav,

  setWallet,
  setWallets,
  toggleReloadWallets,
  setAccounts,
  toggleReloadAccounts,
};
